import {
    CellStyle,
    CellStyleFunc,
    ColDef,
    ValueFormatterParams,
    ValueGetterFunc,
    ValueParserParams,
    ValueSetterFunc,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { GroupedOption } from 'src/components/atoms/Select/Select';
import { EditableValueCell } from 'src/components/molecules/EditableCell/EditableCell';
import { SelectCellEditor } from 'src/components/molecules/SelectCellEditor/SelectCellEditor';
import { rotatedCellWidth } from 'src/components/views/StoreInventoryManagement/data/columns';
import { StoreInventoryRow } from 'src/domain/table/store-inventory.row';
import { useChainFurnitureZoneQuery } from 'src/hooks/chain-furniture-zone/queries/useChainFurnitureZoneQuery';
import { useFurnitureTypeQuery } from 'src/hooks/inventory-master-data/queries/useFurnitureTypesQuery';
import { useInventoryMasterDataQuery } from 'src/hooks/inventory-master-data/queries/useInventoryMasterDataQuery';
import { useStorefrontType } from 'src/hooks/style-timeline/useStorefrontType';
import { useChainStores } from 'src/hooks/useChainStores';
import { parseNumber } from 'src/utils/formatNumber';
import {
    getFurnitureZoneCellState,
    isFurnitureColumnType,
    isPersistedFurnitureColumnType,
} from 'src/utils/furnitureUtils';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';
import { useChainStoresInventoryQuery } from '../queries/useStoreInventoryData';

function furnitureColumnValueFormatter({ value }: ValueFormatterParams): string {
    if (value) {
        return value;
    }
    // Hide zeros for better readability
    return '';
}

function furnitureColumnValueGetter(fieldName: string): ValueGetterFunc<StoreInventoryRow, unknown> {
    return ({ data, node }) => {
        if (node?.group) {
            return node.aggData?.[fieldName] ?? 0;
        }
        const cellData = data?.furnitureColumns?.[fieldName];
        return isFurnitureColumnType(cellData) ? cellData.optionCount : 0;
    };
}

function furnitureColumnValueSetter(
    fieldName: string
): ValueSetterFunc<StoreInventoryRow, string | number | undefined | null> {
    return ({ data, newValue }) => {
        const cellData = data?.furnitureColumns?.[fieldName];
        if (isFurnitureColumnType(cellData)) {
            const parsedValue = parseNumber(newValue);
            if (!Number.isNaN(parsedValue)) {
                cellData.optionCount = parsedValue;
                return true;
            }
        }
        return false;
    };
}

function furnitureColumnCellStyle(
    furnitureType: string,
    furnitureTypeColorPalette: Record<string, string>
): CellStyleFunc<StoreInventoryRow, unknown> {
    return ({ node }) => {
        const styles: CellStyle = { justifyContent: 'center' };

        if (node?.group) {
            styles.fontWeight = 'bold';
        }

        const color = furnitureTypeColorPalette
            ? furnitureTypeColorPalette[furnitureType] || 'transparent'
            : 'transparent';
        styles.backgroundColor = color;

        return styles;
    };
}

export const FURNITURE_COLUMN_KEY_DELIMITER = '*|*';

export const getFurnitureColumnPrefix = (furnitureType: { id: number; name: string } | undefined) => {
    return `${furnitureType?.id}${FURNITURE_COLUMN_KEY_DELIMITER}${furnitureType?.name ?? 'Unknown'}`;
};

export const toFurnitureColumnKey = (
    furnitureType: { id: number; name: string } | undefined,
    item: { id: number; name: string }
) => {
    return `${getFurnitureColumnPrefix(furnitureType)}${FURNITURE_COLUMN_KEY_DELIMITER}${item.name}`;
};

export const useStoreInventoryRows = (includeFurnitureZones = false) => {
    const stores = useChainStores();
    const { data: inventoryMasterData, loading: inventoryLoading } = useInventoryMasterDataQuery();
    const { data: furnitureTypes, loading: furnitureTypesLoading } = useFurnitureTypeQuery();
    const { data: furnitureZones, loading: furnitureZonesLoading } = useChainFurnitureZoneQuery(includeFurnitureZones);
    const storefrontType = useStorefrontType();
    const {
        data: allStoreInventoryData,
        lookup: allStoreInventoryDataLookup,
        loading: allStoresLoading,
    } = useChainStoresInventoryQuery(storefrontType);

    /*
     * There should be a row pr. store, that has the cluster, sales floor square meter and store name. In addition all furnitures should be listed as columns, grouped by type (e.g. Wall, Rack, Table, etc.)
     * Furniture data not available in store inventory data (option count per furniture per store), need to be joined from inventory master data and the default option count is set to 0.
     */

    // Map stores to rows
    const rows = useMemo<StoreInventoryRow[]>(() => {
        if (!inventoryMasterData || !furnitureTypes || !allStoreInventoryData) return [];

        return (stores ?? []).map((store) => {
            const row: StoreInventoryRow = {
                id: store.id,
                clusterName: store.cluster?.name ?? 'N/A',
                storeName: store.storeName,
                countryName: store.country?.name ?? 'N/A',
                storeSize: store.salesFloorSquaremeters ?? 0,
                optionSum: 0,
            };

            // Add furniture columns
            for (const item of inventoryMasterData || []) {
                const storeItem = allStoreInventoryDataLookup.get(store.id)?.get(item.id);
                const furnitureType = furnitureTypes?.find((type) => type.id === item.furnitureTypeId);
                const furnitureTypeName = furnitureType?.name ?? 'Unknown';

                const result = storeItem
                    ? {
                          optionCount: storeItem.numberOfOptions,
                          furnitureType: furnitureTypeName,
                          chainFurnitureId: storeItem.chainFurnitureId,
                          chainFurnitureZoneId: storeItem.chainFurnitureZoneId,
                          storeFurnitureExists: true,
                      }
                    : {
                          optionCount: 0,
                          furnitureType: furnitureTypeName,
                          chainFurnitureId: item.id,
                          chainFurnitureZoneId: null,
                          storeFurnitureExists: false,
                      };
                row.furnitureColumns = {
                    ...row.furnitureColumns,
                    [toFurnitureColumnKey(furnitureType, item)]: result,
                };
            }
            return row;
        });
    }, [allStoreInventoryDataLookup, furnitureTypes, inventoryMasterData, allStoreInventoryData, stores]);

    const furnitureTypeColorPalette = useMemo<Record<string, string>>(() => {
        return (
            furnitureTypes?.reduce<Record<string, string>>((acc, type, index) => {
                const colors = ['#E3F2FD', '#FCE4EC', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F7FA'];
                acc[type.name] = colors[index % colors.length];
                return acc;
            }, {}) ?? {}
        );
    }, [furnitureTypes]);

    const furnitureColumns: ColDef[] = useMemo(() => {
        if (!furnitureTypes || !inventoryMasterData) return [];

        const columns: ColDef<StoreInventoryRow>[] = [];
        for (const item of inventoryMasterData || []) {
            const furniture = furnitureTypes?.find((type) => type.id === item.furnitureTypeId);
            const furnitureType = furniture?.name ?? 'Unknown';
            const fieldName = toFurnitureColumnKey(furniture, item);
            columns.push({
                field: `furnitureColumns.${fieldName}`,
                headerName: `${item.name}`,
                width: rotatedCellWidth,
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
                cellStyle: furnitureColumnCellStyle(furnitureType, furnitureTypeColorPalette),
                headerClass: 'rotate-header-cell',
                editable: true,
                aggFunc: 'sum',
                valueGetter: furnitureColumnValueGetter(fieldName),
                valueSetter: furnitureColumnValueSetter(fieldName),
                valueFormatter: furnitureColumnValueFormatter,
            });
        }

        columns.sort((a, b) => {
            const aType = a.field?.replace('furnitureColumns.', '').split(FURNITURE_COLUMN_KEY_DELIMITER)[0] || '';
            const bType = b.field?.replace('furnitureColumns.', '').split(FURNITURE_COLUMN_KEY_DELIMITER)[0] || '';
            return aType.localeCompare(bType);
        });

        for (const type of furnitureTypes ?? []) {
            columns.push({
                headerName: `${type.name} count`,
                field: `furnitureColumns.${type.name}_optionSum`,
                width: rotatedCellWidth,
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
                pinned: 'right',
                aggFunc: 'sum',
                headerClass: 'rotate-header-cell',
                cellStyle({ node }) {
                    const styles: CellStyle = { justifyContent: 'center' };

                    if (node?.group) {
                        styles.fontWeight = 'bold';
                    }

                    const color = furnitureTypeColorPalette
                        ? furnitureTypeColorPalette[type.name] || 'transparent'
                        : 'transparent';
                    styles.backgroundColor = color;

                    return styles;
                },
                valueGetter({ data, node }) {
                    if (node?.group) {
                        return node.aggData?.[`${type.name}_optionSum`] ?? 0;
                    }
                    // Sum all furniture of this type for the store
                    let sum = 0;
                    for (const key of Object.keys(data?.furnitureColumns || {})) {
                        if (key.startsWith(`${getFurnitureColumnPrefix(type)}`)) {
                            const cellData = data!.furnitureColumns![key];
                            if (isFurnitureColumnType(cellData)) {
                                sum += cellData.optionCount;
                            }
                        }
                    }
                    return sum;
                },
                valueFormatter({ value }) {
                    if (value) {
                        return numberWithSpaces(value);
                    }
                    // Hide zeros for better readability
                    return '';
                },
            });
        }

        return columns;
    }, [furnitureTypes, inventoryMasterData, furnitureTypeColorPalette]);

    const furnitureZoneColumns: ColDef<StoreInventoryRow>[] = useMemo(() => {
        if (!furnitureTypes || !inventoryMasterData || !furnitureZones) return [];

        const selectOptions: GroupedOption[] =
            furnitureZones.length > 0
                ? furnitureZones.map((zone) => ({
                      display: zone.name,
                      value: zone.id.toString(),
                      isHeader: false,
                  }))
                : [
                      {
                          display: 'No zones available',
                          value: '',
                          isHeader: true,
                      },
                  ];

        return inventoryMasterData
            .map((item): ColDef<StoreInventoryRow> => {
                const furniture = furnitureTypes.find((type) => type.id === item.furnitureTypeId);
                const furnitureType = furniture?.name ?? 'Unknown';
                const fieldName = toFurnitureColumnKey(furniture, item);

                return {
                    field: `furnitureColumns.${fieldName}`,
                    headerName: item.name,
                    width: 180,
                    filter: 'agSetColumnFilter',
                    cellStyle: furnitureColumnCellStyle(furnitureType, furnitureTypeColorPalette),
                    headerClass: 'rotate-header-cell',
                    cellClassRules: {
                        'store-inventory-zone-cell--editable': ({ data }) => {
                            const cellData = data?.furnitureColumns?.[fieldName];
                            return getFurnitureZoneCellState(cellData).editable;
                        },
                        'store-inventory-zone-cell--readonly': ({ data }) => {
                            const cellData = data?.furnitureColumns?.[fieldName];
                            return !getFurnitureZoneCellState(cellData).editable;
                        },
                    },
                    editable: ({ data }) => {
                        const cellData = data?.furnitureColumns?.[fieldName];
                        return getFurnitureZoneCellState(cellData).editable;
                    },
                    cellRenderer: EditableValueCell,
                    cellEditor: SelectCellEditor,
                    cellEditorParams: { selectOptions },
                    valueGetter: ({ data, node }) => {
                        if (node?.group || node?.footer || node?.rowPinned) return null;
                        const cellData = data?.furnitureColumns?.[fieldName];
                        return getFurnitureZoneCellState(cellData).value;
                    },
                    valueSetter: ({ data, newValue }) => {
                        const cellData = data?.furnitureColumns?.[fieldName];
                        if (!isPersistedFurnitureColumnType(cellData)) return false;
                        cellData.chainFurnitureZoneId = newValue == null || newValue === '' ? null : Number(newValue);
                        return true;
                    },
                    valueParser: (params: ValueParserParams) => {
                        const pastedValue = params.newValue?.toString();
                        const selectedZone = selectOptions.find(
                            (option) => option.value === pastedValue || option.display === pastedValue
                        );
                        return selectedZone ? Number(selectedZone.value) : null;
                    },
                    valueFormatter: ({ value, node }) => {
                        if (node?.group || node?.footer || node?.rowPinned) return '';
                        if (!value) return '';
                        return selectOptions.find((option) => option.value === value.toString())?.display ?? '';
                    },
                };
            })
            .sort((first, second) => {
                const firstType = first.field?.split(FURNITURE_COLUMN_KEY_DELIMITER)[0] ?? '';
                const secondType = second.field?.split(FURNITURE_COLUMN_KEY_DELIMITER)[0] ?? '';
                return firstType.localeCompare(secondType);
            });
    }, [furnitureTypes, furnitureTypeColorPalette, furnitureZones, inventoryMasterData]);

    return {
        data: rows,
        furnitureColumns,
        furnitureZoneColumns,
        furnitureTypeColorPalette,
        loading:
            inventoryLoading ||
            furnitureTypesLoading ||
            (includeFurnitureZones && furnitureZonesLoading) ||
            allStoresLoading,
    };
};
